import { Button, Typography } from '@mui/material';
import { ScheduleType, type BackupSchedule } from '@repo/api-gw-sdk';
import { forwardRef } from 'react';

import OptionMenu, {
  menuItemStyles,
} from '@/components/option-menu/option-menu';
import type { OptionType } from '@/components/option-menu/option-menu';
import { useFeatureFlags } from '@/contexts/useFeatureFlags';

import {
  annuallyFrequency,
  dailyFrequency,
  getFrequencyByWindow,
  getWindowHoursDuration,
  highFrequencyOptions,
  hourlyFrequency,
  monthlyFrequency,
  pitrFrequencyOptions,
  weeklyFrequency,
  type Frequency,
} from '../frequency';

const frequencyOptions: Frequency[] = [
  hourlyFrequency,
  dailyFrequency,
  weeklyFrequency,
  monthlyFrequency,
  annuallyFrequency,
];

export const FrequencyWindowSelector = (props: {
  schedule: BackupSchedule;
  onUpdate: (schedule: BackupSchedule) => void;
  maxTimesPerDay: number;
}) => {
  const { enableHourlyBackup } = useFeatureFlags();

  const updateWindow = (window: string) => {
    const frequency = getFrequencyByWindow(window);
    if (!frequency) {
      return;
    }

    props.onUpdate({
      ...props.schedule,
      windows: frequency.windows,
      backupRetention: frequency.defaultRetentionInDays,
      duration: getWindowHoursDuration(frequency) * 60,
    });
  };

  const frequency = getFrequencyByWindow(props.schedule.windows);
  const highOptions = highFrequencyOptions.filter(
    (x) => x.timesPerDay && x.timesPerDay <= props.maxTimesPerDay
  );

  const options: OptionType[] =
    props.schedule.scheduleType === ScheduleType.SCHEDULE_TYPE_PITR
      ? pitrFrequencyOptions.map((r) => ({
          id: r.windows,
          text: r.text.toLowerCase(),
          menuItemProps: {
            sx: (theme) =>
              menuItemStyles(frequency === r, theme.palette.mode === 'dark'),
          },
        }))
      : [
          ...frequencyOptions
            .filter(
              (freq) =>
                (enableHourlyBackup && !highOptions.includes(freq)) ||
                freq !== hourlyFrequency
            )
            .map<OptionType>((r) => ({
              id: r.windows,
              text: r.text.toLowerCase(),
              menuItemProps: {
                sx: (theme) =>
                  menuItemStyles(
                    frequency === r,
                    theme.palette.mode === 'dark'
                  ),
              },
            })),
          {
            divider: true,
            id: 'create-backup-policy-frequency-divider',
          },
          {
            text: 'More frequently',
            id: 'create-backup-policy-high-frequency',
            subMenu: highOptions.map((r) => ({
              id: r.windows,
              text: r.text.toLowerCase(),
              menuItemProps: {
                sx: (theme) =>
                  menuItemStyles(
                    frequency === r,
                    theme.palette.mode === 'dark'
                  ),
              },
            })),
          },
        ];

  return (
    <OptionMenu
      leftAlignMenu
      onOptionSelected={(option) => {
        if (typeof option !== 'string') {
          updateWindow(option.id || '');
        }
      }}
      options={options}
      Component={TriggerButton}
      componentProps={{ buttonText: frequency?.text.toLowerCase() }}
    />
  );
};

const TriggerButton = forwardRef<HTMLButtonElement, { buttonText: string }>(
  (props, ref) => {
    const { buttonText, ...rest } = props;

    return (
      <Button
        sx={{
          padding: '5px 12px',
        }}
        data-testid='create-backup-policy-frequency-select'
        disableFocusRipple
        ref={ref}
        {...rest}
      >
        <Typography fontWeight={600}>{buttonText}</Typography>
        <i className='material-symbols-arrow-drop-down-rounded text-[22px] ml-[4px]' />
      </Button>
    );
  }
);
